import { Task } from "../../../pojo/Task";
import { Contact } from "../../../pojo/Contact";
import { S25Number } from "../../../util/core/s25.number";
import { S25Assert } from "../../../util/s25.assert";
import { S25Icons } from "../../s25-icon/s25.icon.const";
import { Workflow } from "../service/Workflow";
import { WorkflowService } from "../service/workflow.service";
import {
    FieldType,
    INGESTION_POINT_TYPE_ID,
    isNameResolvableFieldType,
    JSON_FIELD_TYPES,
    NameResolvableFieldType,
} from "../../data-ingestion/data.ingestion.const";

export type StageStatus = "completed" | "current" | "pending" | "denied" | "cancelled" | "changes_requested";

export interface StageStatusInfo {
    status: StageStatus;
    label: string;
}

export type StageBaseType = "start" | "progress" | "terminal" | "accumulator";
export type StageColorType = StageBaseType | "apAccumulator" | "objectApprovals";

export interface StageTypeStyle {
    label: string;
    glyph: string;
    /** Lightest stop first. */
    gradient: [string, string];
}

export const StageTypeStyles: Record<StageColorType, StageTypeStyle> = {
    start: { label: "Start", glyph: "▶", gradient: ["#5a6c84", "#42526e"] },
    progress: { label: "Progress", glyph: "●", gradient: ["#0065ff", "#0052cc"] },
    accumulator: { label: "Accumulator", glyph: "⊕", gradient: ["#8777d9", "#6554c0"] },
    apAccumulator: { label: "AP Accumulator", glyph: "≡", gradient: ["#0b6870", "#08525a"] },
    objectApprovals: { label: "Object Approvals", glyph: "◆", gradient: ["#8f1f5a", "#741848"] },
    terminal: { label: "Terminal", glyph: "✓", gradient: ["#00a86b", "#00875a"] },
};

export const LegendOrder: StageColorType[] = [
    "start",
    "progress",
    "accumulator",
    "apAccumulator",
    "objectApprovals",
    "terminal",
];

export function stageBaseType(stage: Workflow.WorkflowStage): StageBaseType {
    if (stage.isStartStage) return "start";
    if (stage.isTerminal) return "terminal";
    if (stage.isAccumulator) return "accumulator";
    return "progress";
}

/** Flags recolour only Progress and Accumulator stages; AP Accumulator wins over Object Approvals. */
export function stageColorType(stage: Workflow.WorkflowStage): StageColorType {
    const base = stageBaseType(stage);
    if (base === "start" || base === "terminal") return base;
    if (stage.isApAccumulator) return "apAccumulator";
    if (stage.hasObjectApprovals) return "objectApprovals";
    return base;
}

/** Every applicable flag, named in text on the node whether or not it sets the colour. */
export function stageFlagLabels(stage: Workflow.WorkflowStage): string[] {
    const labels: string[] = [];
    if (stage.isApAccumulator) labels.push(StageTypeStyles.apAccumulator.label);
    if (stage.hasObjectApprovals) labels.push(StageTypeStyles.objectApprovals.label);
    return labels;
}

export const RESTRICTED_CONTACT_LABEL = "Restricted contact";
export const RESTRICTED_CONTACT_REASON = "You don't have access to this contact";
export const RESTRICTED_VALUE_LABEL = "Restricted";

export interface DiagramPersonRow {
    kind: "person";
    key: string;
    contId?: number;
    /** Display name; "Restricted contact" when restricted. */
    name: string;
    restricted: boolean;
    chattable: boolean;
    unavailableReason?: string;
    chatUser?: Workflow.DiagramChatUser;
    status?: Task.State;
    /** Short role shown under the name, e.g. "Submitter" or "Assignee". */
    role?: string;
}

export interface DiagramAiRow {
    kind: "ai";
    key: string;
    stateName?: string;
    /** Absent on definition-only hosts: presence only. */
    outcome?: Workflow.DiagramAiOutcome;
}

export interface DiagramConfiguredRow {
    kind: "configured";
    key: string;
    label: string;
    detail: "Contact" | "Organization role" | "Approval group";
}

export type DiagramApprovalRow = DiagramPersonRow | DiagramAiRow | DiagramConfiguredRow;

export function toPersonRow(person: Workflow.DiagramPerson, keyPrefix: string, role?: string): DiagramPersonRow {
    const restricted = person.restricted || !person.name;
    const chattable = !restricted && person.chattable;
    return {
        kind: "person",
        key: `${keyPrefix}-${person.contId}`,
        contId: person.contId,
        name: restricted ? RESTRICTED_CONTACT_LABEL : person.name!,
        restricted,
        chattable,
        unavailableReason: restricted ? undefined : person.unavailableReason,
        chatUser: chattable ? person.chatUser : undefined,
        status: person.approvalStatus,
        role,
    };
}

function isResolvedStatus(status?: Task.State) {
    return (
        status === Task.States.Completed ||
        status === Task.States.Denied ||
        status === Task.States.Cancelled ||
        status === Task.States.RequestedChanges
    );
}

/** Live hosts: AI rows first (one per AI-reviewed state), then human reviewers. */
export function liveApprovalRows(
    detail: Workflow.DiagramStageDetail | undefined,
    stateCount: number,
): DiagramApprovalRow[] {
    if (!detail) return [];
    const aiRows: DiagramAiRow[] = (detail.aiOutcomes ?? []).map((aiOutcome) => ({
        kind: "ai",
        key: `ai-${aiOutcome.originalWorkflowStateId}`,
        stateName: stateCount > 1 ? aiOutcome.stateName : undefined,
        outcome: aiOutcome.outcome,
    }));
    const reviewers = (detail.reviewers ?? []).filter((reviewer) => reviewer.contId !== Contact.AI_CONTACT_ID);
    const hasResolved = reviewers.some((reviewer) => isResolvedStatus(reviewer.approvalStatus));
    const shownReviewers = hasResolved
        ? reviewers.filter((reviewer) => reviewer.approvalStatus !== Task.States.InProgress)
        : reviewers;
    return [...aiRows, ...shownReviewers.map((reviewer) => toPersonRow(reviewer, "reviewer"))];
}

/** Definition-only hosts: configured approvers per state plus an AI presence row. */
export function configuredApprovalRows(
    states: Workflow.WorkflowState[],
    contIdToName: Record<number, string> | undefined,
    groupIdToName: Map<number, string> | undefined,
): DiagramApprovalRow[] {
    const rows: DiagramApprovalRow[] = [];
    const seenKeys = new Set<string>();
    const add = (row: DiagramApprovalRow) => {
        if (seenKeys.has(row.key)) return;
        seenKeys.add(row.key);
        rows.push(row);
    };
    for (const state of states) {
        if (state.hasAiApprover) {
            add({
                kind: "ai",
                key: `ai-${state.workflowStateId ?? state.id ?? state.stateName}`,
                stateName: states.length > 1 ? state.stateName : undefined,
            });
        }
        if (state.approvalGroupId != null) {
            add({
                kind: "configured",
                key: `group-${state.approvalGroupId}`,
                label: groupIdToName?.get(state.approvalGroupId) ?? "Approval group",
                detail: "Approval group",
            });
        }
        for (const contact of state.contacts ?? []) {
            add({
                kind: "configured",
                key: `contact-${contact.contId}`,
                label: contIdToName?.[contact.contId] ?? contact.contactName ?? RESTRICTED_CONTACT_LABEL,
                detail: "Contact",
            });
        }
        for (const orgRole of state.orgRoles ?? []) {
            add({
                kind: "configured",
                key: `role-${orgRole.contRoleId}`,
                label: orgRole.contRole?.roleName ?? RESTRICTED_VALUE_LABEL,
                detail: "Organization role",
            });
        }
    }
    return rows;
}

export type StatusTone = "approved" | "denied" | "changes" | "cancelled" | "waiting" | "deferred" | "configured";

export interface StatusDisplay {
    label: string;
    icon: S25Icons.Name;
    tone: StatusTone;
}

export function approvalStatusDisplay(status?: Task.State): StatusDisplay {
    switch (status) {
        case Task.States.Completed:
            return { label: "Approved", icon: "check", tone: "approved" };
        case Task.States.Denied:
            return { label: "Denied", icon: "no", tone: "denied" };
        case Task.States.RequestedChanges:
            return { label: "Changes Requested", icon: "pencil", tone: "changes" };
        case Task.States.Cancelled:
            return { label: "Canceled", icon: "close", tone: "cancelled" };
        default:
            return { label: "Awaiting Approval", icon: "clock", tone: "waiting" };
    }
}

export function aiOutcomeDisplay(outcome?: Workflow.DiagramAiOutcome): StatusDisplay {
    switch (outcome) {
        case undefined:
            return { label: "Reviews each submission", icon: "aiSparkles", tone: "configured" };
        case "WAITING":
            return { label: "Waiting", icon: "clock", tone: "waiting" };
        case "APPROVED":
            return { label: "Approved", icon: "check", tone: "approved" };
        case "DENIED":
            return { label: "Denied", icon: "no", tone: "denied" };
        case "CHANGES_REQUESTED":
            return { label: "Changes Requested", icon: "pencil", tone: "changes" };
        case "DEFERRED":
            return { label: "Deferred to reviewers", icon: "share", tone: "deferred" };
        default:
            return S25Assert.never(outcome, "Unknown AI outcome");
    }
}

export const SPAWNED_ROW_CAP = 3;

export interface DiagramSpawnedFormRow {
    key: string;
    name: string;
    submitted: boolean;
    assignee?: DiagramPersonRow;
    currentStageName?: string;
    approvers: DiagramPersonRow[];
}

export interface DiagramSpawnedGroup {
    key: string;
    forms: DiagramSpawnedFormRow[];
    visibleForms: DiagramSpawnedFormRow[];
    hiddenCount: number;
    submittedCount: number;
    rollupLabel: string;
}

export function spawnedGroup(key: string, forms: Workflow.DiagramSpawnedForm[]): DiagramSpawnedGroup {
    const rows: DiagramSpawnedFormRow[] = forms.map((form) => ({
        key: `${form.itemTypeId}-${form.itemId}`,
        name: form.name || "Untitled form",
        submitted: form.submitted,
        assignee: form.assignee ? toPersonRow(form.assignee, `assignee-${form.itemId}`, "Assignee") : undefined,
        currentStageName: form.currentStageName,
        approvers: (form.approvers ?? []).map((approver) => toPersonRow(approver, `spawned-approver-${form.itemId}`)),
    }));
    const submittedCount = rows.filter((row) => row.submitted).length;
    return {
        key,
        forms: rows,
        visibleForms: rows.slice(0, SPAWNED_ROW_CAP),
        hiddenCount: Math.max(0, rows.length - SPAWNED_ROW_CAP),
        submittedCount,
        rollupLabel: `${submittedCount} of ${rows.length} submitted`,
    };
}

export interface DiagramInputRow {
    label: string;
    value: string;
    restricted: boolean;
    isJson: boolean;
}

export interface DiagramActionRow {
    id: string;
    actionId?: number;
    type: Workflow.WorkflowAction["actionType"];
    code?: Workflow.WorkflowAction["actionCode"];
    label: string;
    configured: Workflow.WorkflowAction;
    /** Live hosts only, when diagram.json has this action. */
    detail?: Workflow.DiagramActionDetail;
    isSpawn: boolean;
    /** Spawn actions: names of the forms this action creates submissions for. */
    spawnFormNames?: string[];
    /** Live hosts only: forms this Spawn action created. */
    spawned?: DiagramSpawnedGroup;
}

/** Forms a Spawn action targets: its child form context, plus every dispatch mapping target. */
export function spawnTargetFormIds(action: Workflow.WorkflowAction): number[] {
    if (action.actionCode !== "SPAWN_WORKFLOW") return [];
    const formIds: number[] = [];
    if (action.spawnChildParentItemId != null && action.spawnChildParentItemTypeId === INGESTION_POINT_TYPE_ID) {
        formIds.push(action.spawnChildParentItemId);
    }
    for (const mapping of action.dispatchMappings ?? []) {
        if (mapping.targetIngestionPointId != null) formIds.push(mapping.targetIngestionPointId);
    }
    return [...new Set(formIds)];
}

export function spawnFormLabel(formId: number, formIdToName: Map<number, string> | undefined): string {
    return formIdToName?.get(formId) || `Form ${formId}`;
}

export function actionLabel(
    type: Workflow.WorkflowAction["actionType"] | string,
    code?: Workflow.WorkflowAction["actionCode"],
): string {
    if (code) return WorkflowService.SYSTEM_ACTIONS[code]?.displayName || code;
    return type === "HTTP_REQUEST" ? "HTTP Request" : "System Action";
}

export function inputLabel(code: Workflow.WorkflowAction["actionCode"] | undefined, inputCode: string): string {
    const systemAction: Workflow.SystemAction | undefined = code ? WorkflowService.SYSTEM_ACTIONS[code] : undefined;
    return systemAction?.inputs?.find((systemInput) => systemInput.inputCode === inputCode)?.label || inputCode;
}

/** Live hosts keep only the actions the server ran for this submission's form, in its order. */
export function filterUsedActions(
    actions: Workflow.WorkflowAction[],
    usedActionIds: number[] | undefined,
): Workflow.WorkflowAction[] {
    if (!usedActionIds) return actions;
    const idToAction = new Map<number, Workflow.WorkflowAction>();
    for (const action of actions) {
        if (action.workflowActionId != null) idToAction.set(action.workflowActionId, action);
    }
    const seen = new Set<number>();
    const used: Workflow.WorkflowAction[] = [];
    for (const actionId of usedActionIds) {
        const action = idToAction.get(actionId);
        if (!action || seen.has(actionId)) continue;
        seen.add(actionId);
        used.push(action);
    }
    return used;
}

export function executionOutcomeLabel(execution?: Workflow.DiagramActionDetail["execution"]): string {
    const status = execution?.status;
    switch (status) {
        case undefined:
            return "Not run yet";
        case "COMPLETED":
            return "Completed";
        case "ERROR":
            return "Error";
        case "RUNNING":
            return "Running";
        default:
            return S25Assert.never(status, "Unknown execution status");
    }
}

/** Value keys from {@link DataIngestionService.getObjectNames}: `${id}-${fieldType}`. */
export type ObjectNames = Record<string, string>;

function parseIdList(raw: string): number[] | undefined {
    if (!raw) return undefined;
    const ids = raw.split(",").map((part) => S25Number.parseInt(part.trim()));
    return ids.every((id) => id != null && !Number.isNaN(id)) ? ids : undefined;
}

export function formatResolvedValue(
    input: Workflow.DiagramResolvedInput,
    names: ObjectNames | undefined,
): DiagramInputRow {
    const base = { label: input.inputCode, restricted: false, isJson: false };
    const raw = input.value?.trim() ?? "";
    if (!raw) return { ...base, value: "Not provided" };

    const fieldType = input.fieldType;
    if (fieldType && isNameResolvableFieldType(fieldType)) {
        const ids = parseIdList(raw);
        if (!ids) return { ...base, value: raw };
        if (!names) return { ...base, value: "Loading…" };
        const labels = ids.map((id) => names[`${id}-${fieldType}`]);
        const restricted = labels.some((label) => !label);
        return {
            ...base,
            value: labels.map((label) => label ?? RESTRICTED_VALUE_LABEL).join(", "),
            restricted,
        };
    }
    if (fieldType && JSON_FIELD_TYPES.has(fieldType)) {
        try {
            return { ...base, value: JSON.stringify(JSON.parse(raw), null, 2), isJson: true };
        } catch {
            return { ...base, value: raw };
        }
    }
    return { ...base, value: raw };
}

/** Ids to resolve to names, grouped by field type, across all resolved inputs. */
export function nameLookups(
    details: Workflow.DiagramStageDetail[] | undefined,
): Map<NameResolvableFieldType, number[]> {
    const inputs = (details ?? []).flatMap((stage) => stage.actions ?? []).flatMap((action) => action.inputs ?? []);
    const fieldTypeToIds = new Map<NameResolvableFieldType, Set<number>>();
    for (const input of inputs) {
        const fieldType = input.fieldType as FieldType | undefined;
        if (!fieldType || !isNameResolvableFieldType(fieldType)) continue;
        const ids = parseIdList(input.value?.trim() ?? "");
        if (!ids) continue;
        const knownIds = fieldTypeToIds.get(fieldType) ?? new Set<number>();
        ids.forEach((id) => knownIds.add(id));
        fieldTypeToIds.set(fieldType, knownIds);
    }
    return new Map([...fieldTypeToIds].map(([fieldType, ids]) => [fieldType, [...ids]]));
}

/** Fixed row heights shared by the layout math and the node CSS. */
export const DiagramRowHeights = {
    flags: 18,
    sectionLabel: 22,
    actionRow: 40,
    spawnedRow: 30,
    moreRow: 28,
    emptyRow: 24,
    rollup: 20,
    personRow: 48,
    sectionGap: 12,
    headerGap: 5,
    bottomPadding: 10,
} as const;

export interface StageBodyParts {
    actions: DiagramActionRow[];
    accumulatorSpawned?: DiagramSpawnedGroup;
    approvalRows: DiagramApprovalRow[];
    submitter?: DiagramPersonRow;
    live: boolean;
}

function spawnedRowsHeight(group: DiagramSpawnedGroup | undefined, live: boolean): number {
    if (!live) return 0;
    if (!group?.forms.length) return DiagramRowHeights.emptyRow;
    return (
        group.visibleForms.length * DiagramRowHeights.spawnedRow + (group.hiddenCount ? DiagramRowHeights.moreRow : 0)
    );
}

/** Height of everything below the header; must match the node CSS row heights. */
export function stageBodyHeight(parts: StageBodyParts): number {
    const sections: number[] = [];
    if (parts.actions.length) {
        let height = DiagramRowHeights.sectionLabel + parts.actions.length * DiagramRowHeights.actionRow;
        for (const action of parts.actions) {
            if (action.isSpawn) height += spawnedRowsHeight(action.spawned, parts.live);
        }
        sections.push(height);
    }
    if (parts.accumulatorSpawned) {
        sections.push(
            DiagramRowHeights.sectionLabel +
                DiagramRowHeights.rollup +
                spawnedRowsHeight(parts.accumulatorSpawned, parts.live),
        );
    }
    if (parts.approvalRows.length) {
        sections.push(DiagramRowHeights.sectionLabel + parts.approvalRows.length * DiagramRowHeights.personRow);
    }
    if (parts.submitter) {
        sections.push(DiagramRowHeights.sectionLabel + DiagramRowHeights.personRow);
    }
    if (!sections.length) return 0;
    return (
        DiagramRowHeights.headerGap +
        sections.reduce((sum, height) => sum + height, 0) +
        (sections.length - 1) * DiagramRowHeights.sectionGap +
        DiagramRowHeights.bottomPadding
    );
}

export interface DiagramState {
    id: string;
    name: string;
    approvalGroupName?: string;
    contacts: { contId: number; name: string }[];
    orgRoleNames: string[];
    hasAiApprover: boolean;
    aiOutcome?: Workflow.DiagramAiOutcome;
}

export interface DiagramStage {
    id: string;
    stageId?: number;
    workflowStageId?: number;
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
    /** Header height including the flags line. */
    headerHeight: number;
    type: StageBaseType;
    colorType: StageColorType;
    flagLabels: string[];
    states: DiagramState[];
    actions: DiagramActionRow[];
    accumulatorSpawned?: DiagramSpawnedGroup;
    approvalRows: DiagramApprovalRow[];
    submitter?: DiagramPersonRow;
    status?: StageStatus;
    statusLabel?: string;
}

export type DiagramSelection =
    | { kind: "stage"; stageId: string }
    | { kind: "action"; stageId: string; actionId: string }
    | { kind: "spawned"; stageId: string; groupKey: string };

export function selectionKey(selection: DiagramSelection): string {
    switch (selection.kind) {
        case "stage":
            return `stage:${selection.stageId}`;
        case "action":
            return `action:${selection.stageId}:${selection.actionId}`;
        case "spawned":
            return `spawned:${selection.stageId}:${selection.groupKey}`;
        default:
            return S25Assert.never(selection, "Unknown diagram selection");
    }
}

export type DiagramPaneContent =
    | { kind: "stage"; stage: DiagramStage }
    | { kind: "action"; stage: DiagramStage; action: DiagramActionRow }
    | { kind: "spawned"; stage: DiagramStage; group: DiagramSpawnedGroup; title: string }
    | { kind: "missing" };

export function resolveSelection(stages: DiagramStage[], selection: DiagramSelection): DiagramPaneContent {
    const stage = stages.find((candidate) => candidate.id === selection.stageId);
    if (!stage) return { kind: "missing" };
    switch (selection.kind) {
        case "stage":
            return { kind: "stage", stage };
        case "action": {
            const action = stage.actions.find((candidate) => candidate.id === selection.actionId);
            return action ? { kind: "action", stage, action } : { kind: "missing" };
        }
        case "spawned": {
            if (stage.accumulatorSpawned?.key === selection.groupKey) {
                return {
                    kind: "spawned",
                    stage,
                    group: stage.accumulatorSpawned,
                    title: `Spawned forms — ${stage.name}`,
                };
            }
            const action = stage.actions.find((candidate) => candidate.spawned?.key === selection.groupKey);
            return action?.spawned
                ? { kind: "spawned", stage, group: action.spawned, title: `Spawned forms — ${action.label}` }
                : { kind: "missing" };
        }
        default:
            return S25Assert.never(selection, "Unknown diagram selection");
    }
}

export function paneHeading(content: DiagramPaneContent): string {
    switch (content.kind) {
        case "stage":
            return content.stage.name;
        case "action":
            return content.action.label;
        case "spawned":
            return content.title;
        case "missing":
            return "Details unavailable";
        default:
            return S25Assert.never(content, "Unknown pane content");
    }
}

export interface DiagramChatRequest {
    contId: number;
    name: string;
    chatUser?: Workflow.DiagramChatUser;
    /** Element that should get focus back when the chat closes. */
    returnFocusTo: HTMLElement | null;
}

export function chatRequest(person: DiagramPersonRow, trigger: HTMLElement): DiagramChatRequest | undefined {
    if (person.contId == null) return undefined;
    return { contId: person.contId, name: person.name, chatUser: person.chatUser, returnFocusTo: trigger };
}
